import { useTranslation } from "react-i18next";
import { AMRAP_STOP_RIR_MAX, type AmrapStop, type BlockMember } from "@heraion/shared";

import { amrapStopSentence, amrapStopShort } from "../amrapStopWords";
import { ChipChoiceRow } from "../primitives/ChipChoiceRow";
import type { UiTheme } from "../theme";

type Row = BlockMember["setRows"][number];
type Choice = "none" | "technical-failure" | number;

const CHOICES: readonly Choice[] = [
  "none",
  "technical-failure",
  ...Array.from({ length: AMRAP_STOP_RIR_MAX + 1 }, (_, n) => n),
];

const stopOf = (c: Choice): AmrapStop | undefined =>
  c === "none" ? undefined : c === "technical-failure" ? { kind: "technical-failure" } : { kind: "rir", rir: c };

const choiceOf = (s: AmrapStop | undefined): Choice =>
  !s ? "none" : s.kind === "rir" ? s.rir : "technical-failure";

// #355: where an AMRAP set stops, a line under the set like the rep range
// (ComposerSetRange). The host shows it on an AMRAP set only; the rule stays
// on the cell when the "+" goes, and comes back with it.
export function ComposerAmrapStop({
  row,
  setLabel,
  onUpdateRow,
  theme,
}: {
  row: Row;
  /** The set's number as the table prints it, so each group has its own name. */
  setLabel: string;
  onUpdateRow: (patch: Partial<Row>) => void;
  theme: UiTheme;
}) {
  const { t } = useTranslation();
  const none = t("program.amrapStop.none", { defaultValue: "No rule" });
  return (
    <ChipChoiceRow<Choice>
      theme={theme}
      choices={CHOICES}
      selected={choiceOf(row.amrapStop)}
      onSelect={(c) => onUpdateRow({ amrapStop: stopOf(c) })}
      label={(c) => {
        const s = stopOf(c);
        // The row's title says "RIR": the chip is the bare number.
        return !s ? none : s.kind === "rir" ? String(s.rir) : amrapStopShort(t, s);
      }}
      chipA11yLabel={(c) => {
        const s = stopOf(c);
        return s ? amrapStopSentence(t, s) : none;
      }}
      groupA11yLabel={t("program.amrapStop.groupSet", { n: setLabel, defaultValue: "Where AMRAP set {{n}} stops" })}
      title={t("program.amrapStop.title", { defaultValue: "Stop at RIR" })}
    />
  );
}
